import { observer } from '@formily/reactive-vue';
import { connect, mapProps, mapReadPretty, h, useField } from '@formily/vue';
import { ref, defineComponent, watch, onBeforeUnmount } from '@vue/composition-api';
import { Cascader as VanCascader, Popup as VanPopup } from 'vant';
import { formilyFormItem } from './index';
import { PreviewText } from '@formily/vant';

// 根据已选 value 路径，定位到当前正在展示的那一级 options
// 例如 selectedPath = [2]，options 顶层为整棵树时，返回 options[?].children
// 没有匹配到则返回顶层 options
const getCurrentLevelOptions = (options, selectedPath, valueKey) => {
  if (!Array.isArray(options)) return [];
  if (!Array.isArray(selectedPath) || selectedPath.length === 0) return options;
  let level = options;
  for (let i = 0; i < selectedPath.length; i += 1) {
    const v = selectedPath[i];
    const matched = level.find((it) => it && it[valueKey] === v);
    if (!matched || !Array.isArray(matched.children) || !matched.children.length) {
      return level;
    }
    level = matched.children;
  }
  return level;
};

const BaseCascader = observer(
  defineComponent({
    name: 'FBaseCascader',
    props: ['value', 'options'],
    setup(props, { attrs, emit, slots }) {
      const {
        formItemProps = {},
        popupProps = {},
        cascaderProps = {},
        fieldListeners = {},
        popupListeners = {},
        cascaderListeners = {},
      } = attrs;
      const show = ref(false);
      const field = useField();
      const fieldValue = ref([]);

      const valueKey = cascaderProps?.fieldNames?.value || 'value';
      const textKey = cascaderProps?.fieldNames?.text || 'label';

      // 多次轮询调度计时器，便于卸载时清理
      const timers = [];
      const clearTimers = () => {
        while (timers.length) {
          const t = timers.shift();
          clearTimeout(t);
        }
      };

      // 已选路径（value 数组），用于推断每个 tab pane 当前展示的是哪一级 options
      const selectedPath = ref([]);

      // 扫描 DOM，给 disabled 项 li 加 pointer-events:none 等样式
      // 关键修复：按 tab pane 索引定位每一级 options，再按"当前级 li 顺序"
      // 与 options 顺序一一对应，避免不同级 label 同名造成误伤。
      const applyDisabledStyle = () => {
        try {
          const panes = document.querySelectorAll('.van-cascader__options');
          if (!panes || !panes.length) return;

          panes.forEach((pane, paneIndex) => {
            // 第 paneIndex 个 pane 展示的是「按已选路径前 paneIndex 项」深入后的那层 options
            const path = (selectedPath.value || []).slice(0, paneIndex);
            const levelOptions = getCurrentLevelOptions(props.options, path, valueKey);

            const liList = pane.querySelectorAll('.van-cascader__option');
            if (!liList || !liList.length) return;

            liList.forEach((li, liIndex) => {
              const item = levelOptions && levelOptions[liIndex];
              const isDisabled = !!(item && item.disabled === true);

              if (isDisabled) {
                li.setAttribute('data-disabled', 'true');
                li.style.color = '#c8c9cc';
                li.style.cursor = 'not-allowed';
                li.style.pointerEvents = 'none';
              } else if (li.getAttribute('data-disabled') === 'true') {
                // 之前被我们标记，但当前不再命中，需还原
                li.removeAttribute('data-disabled');
                li.style.color = '';
                li.style.cursor = '';
                li.style.pointerEvents = '';
              }
            });
          });
        } catch (err) {
          // 防御：DOM 操作异常不影响主流程
          // eslint-disable-next-line no-console
          console.warn('[Cascader] applyDisabledStyle error:', err);
        }
      };

      // 多次轮询，覆盖 popup 动画 / tab 切换 / 异步渲染 时序
      const scheduleApplyDisabledStyle = () => {
        [0, 50, 150, 300].forEach((delay) => {
          const t = setTimeout(() => {
            applyDisabledStyle();
          }, delay);
          timers.push(t);
        });
      };

      onBeforeUnmount(() => {
        clearTimers();
      });

      watch(
        () => props.value,
        (newVal) => {
          if (Array.isArray(newVal) && Array.isArray(props.options)) {
            let tmp = props.options;
            const res = [];
            newVal.forEach((item) => {
              const target = tmp.find((obj) => obj[valueKey] === item);
              if (!target) return;
              res.push(target[textKey] || target.label || target.text);
              tmp = target.children || [];
            });
            fieldValue.value = res || [];
          }
        },
        { deep: true, immediate: true }
      );

      // 打开 popup 时统一处理：初始化已选路径 + 调度禁用样式刷新
      const openPopup = () => {
        if (field.value.pattern !== 'editable') return;
        selectedPath.value = Array.isArray(props.value) ? [...props.value] : [];
        show.value = true;
        scheduleApplyDisabledStyle();
      };

      return () =>
        h(
          'div',
          {
            style: { cursor: 'pointer' },
            nativeOn: {
              click: () => openPopup(),
            },
            on: {
              click: () => openPopup(),
            },
          },
          {
            default: () => [
              h(
                formilyFormItem,
                {
                  attrs: {
                    ...formItemProps,
                    readonly: true,
                    isLink: field.value.pattern === 'editable',
                  },
                  on: {
                    ...fieldListeners,
                  },
                  nativeOn: {
                    click: () => openPopup(),
                  },
                },
                {
                  ...slots,
                  default: () => [fieldValue.value ? fieldValue.value.join('/') : ''],
                }
              ),
              h(
                VanPopup,
                {
                  attrs: {
                    value: show.value,
                    round: true,
                    position: 'bottom',
                    getContainer: 'body',
                    ...popupProps,
                  },
                  on: {
                    input: (val) => {
                      show.value = val;
                      if (val) {
                        // 打开时按当前 value 初始化已选路径
                        selectedPath.value = Array.isArray(props.value) ? [...props.value] : [];
                        scheduleApplyDisabledStyle();
                      }
                    },
                    opened: () => {
                      scheduleApplyDisabledStyle();
                    },
                    ...popupListeners,
                  },
                },
                {
                  default: () => [
                    h(
                      VanCascader,
                      {
                        attrs: {
                          fieldNames: { text: 'label', value: 'value', children: 'children' },
                          ...cascaderProps,
                          ...props,
                          value: Array.isArray(props.value)
                            ? props.value[props.value.length - 1]
                            : props.value,
                          // 支持通过 x-component-props 的 cascaderTitle 传入自定义标题
                          title: attrs.cascaderTitle || cascaderProps.title || '',
                        },
                        on: {
                          // 先展开外部传入的监听，再用内置事件覆盖以保证拦截优先
                          ...cascaderListeners,
                          close: () => {
                            show.value = false;
                            if (typeof cascaderListeners.close === 'function') {
                              cascaderListeners.close();
                            }
                          },
                          change: (val) => {
                            // tab 切换、单层选中后均会触发
                            // 维护 selectedPath，用于按层级精确定位 disabled 项
                            const sel = (val && val.selectedOptions) || [];
                            selectedPath.value = sel
                              .filter((it) => it && it[valueKey] != null)
                              .map((it) => it[valueKey]);
                            scheduleApplyDisabledStyle();
                            if (typeof cascaderListeners.change === 'function') {
                              cascaderListeners.change(val);
                            }
                          },
                          finish: (val) => {
                            // 兜底：selectedOptions 自带 disabled 字段，按层级精确判断
                            // 不再用全树 value 集合判断，避免不同级 value 同名互相污染
                            const selectedOptions = val && val.selectedOptions ? val.selectedOptions : [];
                            const hitDisabled = selectedOptions.some((item) => item && item.disabled === true);
                            if (hitDisabled) {
                              // 命中禁用项，丢弃此次结果，保持 popup 打开并刷新样式
                              scheduleApplyDisabledStyle();
                              return;
                            }
                            const selected = selectedOptions.map((item) => item[valueKey]);
                            emit('change', selected);
                            show.value = false;
                            if (typeof cascaderListeners.finish === 'function') {
                              cascaderListeners.finish(val);
                            }
                          },
                        },
                      },
                      {}
                    ),
                  ],
                }
              ),
            ],
          }
        );
    },
  })
);

export const Cascader = connect(
  BaseCascader,
  mapProps({ readOnly: 'readonly', dataSource: 'options' }),
  mapReadPretty(PreviewText.Cascader)
);

export default Cascader;
